import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavActions } from '../components/NavBar/NavActions';
import { MobileUnsupported } from '../components/MobileUnsupported';
import { Button } from '../components/SubscriptionAppButton';
import { StatCard } from '../components/StatCard';
import { useTags } from '../contexts/TagsContext';
import { AutomationRule, CATEGORIES, INITIAL_RULES, isChangeAction, RuleAction, SAVED_VIEWS, TEMPLATES } from '../data/mockAutomationRules';
import { MoreVertical, Plus, Sprout, AlertTriangle, Trophy, Wine, Pencil, Copy, Clock, Pause, Play, Trash2, Workflow, Search, X, CheckCircle2, Gauge, ShieldAlert, Filter, ChevronDown, CalendarClock, HeartHandshake, FlaskConical } from 'lucide-react';
const TEMPLATE_ICONS: Record<string, any> = {
  Sprout,
  AlertTriangle,
  Trophy,
  Wine,
  CalendarClock,
  HeartHandshake
};
type Tab = 'All Rules' | 'Active' | 'Testing' | 'Paused' | 'Drafts';
export function AutomationRulesPage() {
  const navigate = useNavigate();
  const {
    allTags
  } = useTags();
  const [rules, setRules] = useState<AutomationRule[]>(INITIAL_RULES);
  const [activeTab, setActiveTab] = useState<Tab>('All Rules');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('All categories');
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [deleteRuleId, setDeleteRuleId] = useState<string | null>(null);
  const [showKillSwitch, setShowKillSwitch] = useState(false);
  const [showSavedViews, setShowSavedViews] = useState(false);
  const [allPaused, setAllPaused] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<any>(null);
  const showToast = (message: string) => {
    setToast(message);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 6000);
  };
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Element;
      if (activeMenuId && !target.closest('.rule-menu-container')) setActiveMenuId(null);
      if (showSavedViews && !target.closest('.saved-views-container')) setShowSavedViews(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeMenuId, showSavedViews]);
  const activeCount = rules.filter(r => r.status === 'active').length;
  const testingCount = rules.filter(r => r.status === 'testing').length;
  const pausedCount = rules.filter(r => r.status === 'paused').length;
  const draftCount = rules.filter(r => r.status === 'draft').length;
  const changeRuleCount = rules.filter(r => r.status !== 'draft' && [...r.metActions, ...r.scoreBands.flatMap(b => b.actions)].some(a => isChangeAction(a.type))).length;
  const filteredRules = useMemo(() => rules.filter(rule => {
    if (activeTab === 'Active' && rule.status !== 'active') return false;
    if (activeTab === 'Testing' && rule.status !== 'testing') return false;
    if (activeTab === 'Paused' && rule.status !== 'paused') return false;
    if (activeTab === 'Drafts' && rule.status !== 'draft') return false;
    if (categoryFilter !== 'All categories' && rule.category !== categoryFilter) return false;
    if (search.trim() && !rule.name.toLowerCase().includes(search.trim().toLowerCase())) return false;
    return true;
  }), [rules, activeTab, categoryFilter, search]);
  const togglePause = (rule: AutomationRule) => {
    const nextStatus = rule.status === 'paused' ? 'active' : 'paused';
    setRules(prev => prev.map(r => r.id === rule.id ? {
      ...r,
      status: nextStatus
    } : r));
    showToast(nextStatus === 'paused' ? `“${rule.name}” paused. It will stop evaluating and stop running exit criteria. Anything it already applied stays on members.` : `“${rule.name}” resumed. It will run again on the next daily run.`);
  };

  // A single switch that stops everything. When a rule starts doing something
  // wrong at 2am, the merchant needs one control, not fourteen toggles.
  const handleKillSwitch = () => {
    const next = !allPaused;
    setAllPaused(next);
    setRules(prev => prev.map(r => r.status === 'draft' ? r : {
      ...r,
      status: next ? 'paused' : 'active'
    }));
    setShowKillSwitch(false);
    showToast(next ? 'All rules paused. Nothing will evaluate or action until you turn them back on.' : 'All rules resumed. They will run on the next daily run.');
  };
  const handleDuplicate = (rule: AutomationRule) => {
    setActiveMenuId(null);
    navigate('/settings/automation-rules/create', {
      state: {
        rule: {
          ...rule,
          name: `${rule.name} — copy`
        }
      }
    });
  };
  const handleDelete = () => {
    if (!deleteRuleId) return;
    const rule = rules.find(r => r.id === deleteRuleId);
    setRules(rules.filter(r => r.id !== deleteRuleId));
    setDeleteRuleId(null);
    if (rule) showToast(`“${rule.name}” deleted.`);
  };
  const getCategoryStyles = (category: string) => {
    switch (category) {
      case 'Lifecycle':
        return 'bg-[#DBEAFE] text-[#1E40AF]';
      case 'Behaviour':
        return 'bg-[#F3E8FF] text-[#7C3AED]';
      case 'Value':
        return 'bg-[#FEF3C7] text-[#B45309]';
      case 'Product':
        return 'bg-[#CCFBF1] text-[#0F766E]';
      case 'Seasonal':
        return 'bg-[#FCE7F3] text-[#BE185D]';
      default:
        return 'bg-[#F1F5F9] text-[#475569]';
    }
  };

  // Tag colour comes from the tag record, not from the rule.
  const getTagStyles = (label: string) => {
    const tag = allTags.find(t => t.label.toLowerCase() === label.toLowerCase());
    switch (tag?.color) {
      case 'green':
        return 'bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0]';
      case 'blue':
        return 'bg-[#DBEAFE] text-[#1E40AF] border-[#BFDBFE]';
      case 'orange':
        return 'bg-[#FED7AA] text-[#C2410C] border-[#FDBA74]';
      case 'purple':
        return 'bg-[#E0E7FF] text-[#3730A3] border-[#C7D2FE]';
      default:
        return 'bg-[#F1F5F9] text-[#475569] border-[#CBD5E1]';
    }
  };
  const renderActionChip = (action: RuleAction, key: string) => {
    if (action.type === 'Add Tag' || action.type === 'Remove Tag') {
      const prefix = action.type === 'Add Tag' ? '+' : '−';
      return <span key={key} className={`text-xs px-2.5 py-0.5 rounded-full font-medium border whitespace-nowrap ${getTagStyles(action.value)}`} data-id="element-7322" data-mp="1yrkzbgc4md4e">
          {prefix} {action.value || 'No tag set'}
        </span>;
    }
    // Actions that change a subscription read differently, because they are.
    const isChange = isChangeAction(action.type);
    const shortLabel = action.type === 'Send Klaviyo Event' ? 'Klaviyo event' : action.type === 'Create Task' ? 'Create task' : action.type === 'Add Note to Contact' ? 'Add note' : action.type === 'Add to Action Required' ? 'Action Required' : action.type === 'Flag as Moment to Reach Out' ? 'Moment' : action.type;
    return <span key={key} className={`text-xs px-2.5 py-0.5 rounded-full font-medium border whitespace-nowrap inline-flex items-center gap-1 ${isChange ? 'bg-[#FFF7ED] text-[#C2410C] border-[#FDBA74]' : 'bg-[#F8FAFC] text-[#475569] border-[#E2E8F0]'}`} data-id="element-7323" data-mp="17hzys3iy54yz">
        {isChange && <ShieldAlert className="w-3 h-3" data-id="element-7324" data-mp="yjxpl0p2760m" />}
        {shortLabel}
      </span>;
  };
  const renderEmptyState = () => {
    const hasFilters = search.trim() !== '' || categoryFilter !== 'All categories';
    if (hasFilters) {
      return <div className="bg-white border border-[#E2E8F0] rounded-lg p-12 text-center" data-id="element-7325" data-mp="jsnp72jpiuw5">
          <Search className="w-8 h-8 text-[#CBD5E1] mx-auto mb-3" data-id="element-7326" data-mp="frbvolxpppio" />
          <h3 className="text-sm font-semibold text-[#0F172A] mb-1" data-id="element-7327" data-mp="ft6k4a86zzri">
            No rules match your filters
          </h3>
          <p className="text-sm text-[#64748B] mb-4" data-id="element-7328" data-mp="5t5xaeb471ik">
            Try a different search or category.
          </p>
          <Button variant="secondary" size="sm" onClick={() => {
          setSearch('');
          setCategoryFilter('All categories');
        }} data-id="element-7329" data-mp="1tq3snwa8yg9k">
            Clear filters
          </Button>
        </div>;
    }
    const copy: Record<Tab, {
      title: string;
      body: string;
    }> = {
      'All Rules': {
        title: 'No automation rules yet',
        body: 'Rules watch your members and respond — tagging them, putting them in front of the team, or changing their subscription. Start from a template below, or turn a saved view into a rule.'
      },
      Active: {
        title: 'No active rules',
        body: 'Nothing is running at the moment. Resume a paused rule or create a new one.'
      },
      Testing: {
        title: 'No rules in testing',
        body: 'A test run evaluates a rule every day and records what it would have done, without actioning anyone. It is the safe way to check a rule before going live.'
      },
      Paused: {
        title: 'No paused rules',
        body: 'Paused rules stop evaluating but keep anything they have already applied.'
      },
      Drafts: {
        title: 'No drafts',
        body: 'A rule saved before it is finished will show up here.'
      }
    };
    return <div className="bg-white border border-[#E2E8F0] rounded-lg p-12 text-center" data-id="element-7330" data-mp="ncqr8g6hytog">
        <Workflow className="w-8 h-8 text-[#CBD5E1] mx-auto mb-3" data-id="element-7331" data-mp="ibuqgbhfznn9" />
        <h3 className="text-sm font-semibold text-[#0F172A] mb-1" data-id="element-7332" data-mp="wcdvrauiwflq">
          {copy[activeTab].title}
        </h3>
        <p className="text-sm text-[#64748B] max-w-md mx-auto mb-4" data-id="element-7333" data-mp="re41r3kjbl1p">
          {copy[activeTab].body}
        </p>
        <Button variant="primary" size="sm" iconLeft={<Plus className="w-4 h-4" data-id="element-7335" data-mp="164jju9hnmcvn" />} onClick={() => navigate('/settings/automation-rules/create')} data-id="element-7334" data-mp="161fa6wlf6oxl">
          Create Rule
        </Button>
      </div>;
  };
  const tabs: {
    key: Tab;
    count: number;
  }[] = [{
    key: 'All Rules',
    count: rules.length
  }, {
    key: 'Active',
    count: activeCount
  }, {
    key: 'Testing',
    count: testingCount
  }, {
    key: 'Paused',
    count: pausedCount
  }, {
    key: 'Drafts',
    count: draftCount
  }];
  return <>
      <div className="block md:hidden" data-id="element-7336" data-mp="3o0httxasy2kd">
        <MobileUnsupported data-id="element-7337" data-mp="3j2zi5z5eh98o" />
      </div>

      <div className="hidden md:block bg-[#F1F5F9] min-h-full p-6 relative" data-id="element-7338" data-mp="3j3rcl6n9h0xd">
        <div className="mb-6" data-id="element-7339" data-mp="3e4o5lgnq0kmr">
          <nav className="w-full h-16 bg-white border-b border-[#CBD5E1] px-6 flex items-center gap-3" data-id="element-7340" data-mp="3e5hfurlw1s9n">
            <h1 className="text-lg font-semibold text-[#0F172A] m-0" data-id="element-7341" data-mp="flr2nly5alh4">
              Automation Rules
            </h1>
            <div aria-hidden="true" className="flex-1" data-id="element-7342" data-mp="al3dz6xe2gq2"></div>

            {/* One control that stops everything. */}
            <Button variant="ghost" onClick={() => setShowKillSwitch(true)} data-id="element-7343" data-mp="1mhvfbrx31pa">
              {allPaused ? 'Resume all rules' : 'Pause all rules'}
            </Button>

            <div className="relative saved-views-container" data-id="element-7344" data-mp="2qfn46ddb29qz">
              <Button variant="secondary" iconLeft={<Filter className="w-4 h-4" data-id="element-7346" data-mp="2mbfeodz0wprp" />} onClick={() => setShowSavedViews(!showSavedViews)} data-id="element-7345" data-mp="2mcgz391xiom3">
                From a saved view
              </Button>
              {showSavedViews && <div className="absolute right-0 top-11 w-72 bg-white rounded-lg shadow-lg border border-[#E2E8F0] py-1 z-30" data-id="element-7347" data-mp="15xb4sg7ld1qk">
                  <div className="px-4 py-2 border-b border-[#E2E8F0]" data-id="element-7348" data-mp="1au1k31bkntax">
                    <p className="text-xs text-[#64748B]" data-id="element-7349" data-mp="1fmusq0a5vlsa">
                      Turn a filter you already use into a rule. The saved view
                      itself is not changed.
                    </p>
                  </div>
                  {SAVED_VIEWS.map(view => <button key={view.id} onClick={() => {
                setShowSavedViews(false);
                navigate('/settings/automation-rules/create', {
                  state: {
                    savedView: view
                  }
                });
              }} className="w-full text-left px-4 py-2.5 hover:bg-[#F8FAFC] transition-colors" data-id="element-7350" data-mp="1tluduxygtijp">
                      <div className="text-sm text-[#0F172A]" data-id="element-7351" data-mp="4763f29fqaob">{view.name}</div>
                      <div className="text-xs text-[#94A3B8]" data-id="element-7352" data-mp="17imply6he0ol">{view.surface}</div>
                    </button>)}
                </div>}
            </div>

            <Button variant="primary" iconLeft={<Plus className="w-4 h-4" data-id="element-7354" data-mp="1gjku0hku0a7p" />} onClick={() => navigate('/settings/automation-rules/create')} data-id="element-7353" data-mp="1gii5uhew8lvh">
              Create Rule
            </Button>
            <NavActions data-id="element-7355" data-mp="1blrpru339qa2" />
          </nav>
        </div>

        <p className="text-sm text-[#64748B] mb-6" data-id="element-7356" data-mp="1qfnxbqrf6ew8">
          Rules watch your members and respond — tagging them, putting them in
          front of the team, or changing their subscription.
        </p>

        {allPaused && <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-lg p-4 mb-6 flex items-center gap-3" data-id="element-7357" data-mp="1z8hlkdougom8">
            <ShieldAlert className="w-5 h-5 text-[#EF4444] flex-shrink-0" data-id="element-7358" data-mp="1v9innd1j5s4a" />
            <p className="text-sm text-[#991B1B] flex-1" data-id="element-7359" data-mp="297yud3ze7imf">
              All rules are paused. Nothing is evaluating or actioning.
            </p>
            <Button variant="secondary" size="sm" onClick={() => setShowKillSwitch(true)} data-id="element-7360" data-mp="2ntw4wdujywbi">
              Resume all
            </Button>
          </div>}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-id="element-7361" data-mp="2srnzxgqhuyjx">
          <StatCard label="Active Rules" value={activeCount} trend={<div className="flex items-center gap-1.5" data-id="element-7363" data-mp="2iuszgsanuo30">
                <div className="w-2 h-2 rounded-full bg-[#10B981]" data-id="element-7364" data-mp="2it84j1kfp34m" />
                <span className="text-xs text-[#10B981] font-medium" data-id="element-7365" data-mp="2534oisc5e5nm">Live</span>
              </div>} data-id="element-7362" data-mp="2duoil15ubrc9" />
          <StatCard label="In Testing" value={testingCount} trend={<div className="flex items-center gap-1.5" data-id="element-7367" data-mp="29w7i30gxdf3t">
                <div className="w-2 h-2 rounded-full bg-[#8B5CF6]" data-id="element-7368" data-mp="1v69uomqtktfo" />
                <span className="text-xs text-[#8B5CF6] font-medium" data-id="element-7369" data-mp="2041rnv6bn5qq">
                  Actioning nobody
                </span>
              </div>} data-id="element-7366" data-mp="29ucz3rp7rx2d" />
          <StatCard label="Rules That Change Subscriptions" value={changeRuleCount} secondaryValue="Capped and logged" data-id="element-7370" data-mp="1m525cu6a72xq" />
          <StatCard label="Members Actioned" value="1,847" secondaryValue="Last 30 days" data-id="element-7371" data-mp="1q88ce417wy59" />
        </div>

        <div className="border-b border-[#E2E8F0] mb-4" data-id="element-7372" data-mp="1j4rxd8uidci8">
          <div className="flex gap-8" data-id="element-7373" data-mp="1e55m34y8q6c2">
            {tabs.map(tab => <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`pb-3 text-sm font-medium transition-colors relative ${activeTab === tab.key ? 'text-[#0EA5E9]' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-7374" data-mp="19cumk37ewmk1">
                {tab.key} ({tab.count})
                {activeTab === tab.key && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#0EA5E9]" data-id="element-7375" data-mp="vdctvcqzwp5e" />}
              </button>)}
          </div>
        </div>

        <div className="flex items-center gap-3 mb-6" data-id="element-7376" data-mp="qfb8gl63kdqr">
          <div className="relative" data-id="element-7377" data-mp="lf6ryv56vj0j">
            <Search className="w-4 h-4 text-[#94A3B8] absolute left-3 top-1/2 -translate-y-1/2" data-id="element-7378" data-mp="lg8fw07azzzo" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search rules…" className="h-[36px] w-64 pl-9 pr-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 transition-all" data-id="element-7379" data-mp="2ksj6smta77h5" />
          </div>
          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="h-[36px] bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 transition-all cursor-pointer" style={{
          paddingLeft: 12,
          paddingRight: 36
        }} data-id="element-7380" data-mp="2sa8yqm4eap2u">
            <option data-id="element-7381" data-mp="29dzdnev2sy9c">All categories</option>
            {CATEGORIES.map(c => <option key={c} value={c} data-id="element-7382" data-mp="2ecapfv4xliu7">
                {c}
              </option>)}
          </select>
          <span className="text-xs text-[#94A3B8]" data-id="element-7383" data-mp="2jaue43awt8q9">
            {filteredRules.length} of {rules.length} rules
          </span>
        </div>

        <div className="space-y-3 mb-10" data-id="element-7384" data-mp="3pqeimz3p32y7">
          {filteredRules.length === 0 ? renderEmptyState() : filteredRules.map((rule, rowIndex) => {
          const isDraft = rule.status === 'draft';
          const isTesting = rule.status === 'testing';
          const isPaused = rule.status === 'paused';
          const flipUp = filteredRules.length > 3 && rowIndex >= filteredRules.length - 2;
          const openRule = () => navigate(`/settings/automation-rules/${rule.id}`);
          const displayActions = rule.ruleType === 'scored' ? rule.scoreBands.flatMap(b => b.actions) : rule.metActions;
          const touchesSubscriptions = displayActions.some(a => isChangeAction(a.type));
          return <div key={rule.id} role="link" tabIndex={0} onClick={openRule} onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openRule();
            }
          }} className={`bg-white border rounded-lg p-4 flex items-center gap-4 transition-all cursor-pointer hover:border-[#0EA5E9] hover:shadow-sm focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 ${touchesSubscriptions ? 'border-[#FDBA74]' : 'border-[#E2E8F0]'} ${isPaused ? 'opacity-60' : ''}`} data-id="element-7385" data-mp="2hdvplb42pcen" data-mpi={rowIndex}>
                {isDraft ? <span className="w-11 flex-shrink-0 flex justify-center" data-id="element-7386" data-mp="iv91ohkiv1w8">
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-[#FEF3C7] text-[#B45309] uppercase tracking-wide" data-id="element-7387" data-mp="iwk2q9ml1vjn">
                      Draft
                    </span>
                  </span> : isTesting ? <span className="w-11 flex-shrink-0 flex justify-center" title="Evaluating daily, actioning nobody" data-id="element-7388" data-mp="wurzagfpiuua">
                    <FlaskConical className="w-5 h-5 text-[#8B5CF6]" data-id="element-7389" data-mp="11tv0tqwhaixi" />
                  </span> : <button role="switch" aria-checked={rule.status === 'active'} aria-label={rule.status === 'active' ? `Pause ${rule.name}` : `Resume ${rule.name}`} onClick={e => {
              e.stopPropagation();
              togglePause(rule);
            }} className={`w-11 h-6 rounded-full relative transition-colors flex-shrink-0 ${rule.status === 'active' ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-7390" data-mp="nu3rw4ktjsqd">
                    <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${rule.status === 'active' ? 'left-6' : 'left-1'}`} data-id="element-7391" data-mp="84cxyn3qec1b" />
                  </button>}

                <div className="w-56 flex-shrink-0 min-w-0" data-id="element-7392" data-mp="3v8j8vuhi4yar">
                  <span className="flex items-center gap-1.5 text-sm font-semibold text-[#0F172A]" data-id="element-7393" data-mp="3v8t3xwhelvnb">
                    <span className="truncate" title={rule.name} data-id="element-7394" data-mp="3qbas9yc052bm">
                      {rule.name}
                    </span>
                    {rule.ruleType === 'scored' && <Gauge className="w-3.5 h-3.5 text-[#6366F1] flex-shrink-0" title="Scored rule" data-id="element-7395" data-mp="mshgnr2sank3" />}
                  </span>
                  {isTesting && <span className="text-[11px] text-[#8B5CF6] font-medium" data-id="element-7396" data-mp="i06jgvzfhfv8">
                      Testing — actioning nobody
                    </span>}
                </div>

                <div className="w-28 flex-shrink-0" data-id="element-7397" data-mp="1aj3pvloiydvo">
                  <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${getCategoryStyles(rule.category)}`} data-id="element-7398" data-mp="15rlqh225glct">
                    {rule.category}
                  </span>
                </div>

                <div className="flex-1 min-w-0 flex items-center gap-1.5 flex-wrap" data-id="element-7399" data-mp="1i1l3a2ziuctx">
                  {displayActions.length === 0 ? <span className="text-xs text-[#94A3B8]" data-id="element-7400" data-mp="1n0ef7qz5tvrz">No actions set</span> : <>
                      {displayActions.slice(0, 2).map((a, i) => renderActionChip(a, `${rule.id}-${i}`))}
                      {displayActions.length > 2 && <span className="text-xs text-[#64748B] font-medium" data-id="element-7401" data-mp="1w0t6r82pw73x">
                          +{displayActions.length - 2}
                        </span>}
                    </>}
                </div>

                <div className="flex items-center gap-4 flex-shrink-0 relative rule-menu-container" data-id="element-7402" data-mp="25rxk63cqvjwn">
                  {isDraft ? <button onClick={e => {
                e.stopPropagation();
                openRule();
              }} className="text-xs text-[#0EA5E9] font-medium hover:underline" data-id="element-7403" data-mp="2app9cpm08rx5">
                      Continue editing
                    </button> : <>
                      <span className="text-xs text-[#94A3B8] whitespace-nowrap" data-id="element-7404" data-mp="3kou8xutv8ukn">
                        {rule.membersMatched} matched
                      </span>
                      <span className="text-xs text-[#94A3B8] whitespace-nowrap" data-id="element-7405" data-mp="3toh07nsvsjzz">
                        Last run: {rule.lastTriggered}
                      </span>
                    </>}

                  <button aria-label={`Actions for ${rule.name}`} onClick={e => {
                e.stopPropagation();
                setActiveMenuId(activeMenuId === rule.id ? null : rule.id);
              }} className={`p-1 rounded-md transition-colors ${activeMenuId === rule.id ? 'bg-[#F1F5F9] text-[#0F172A]' : 'text-[#94A3B8] hover:text-[#64748B] hover:bg-[#F1F5F9]'}`} data-id="element-7406" data-mp="2ftf98yjyysy">
                    <MoreVertical className="w-4 h-4" data-id="element-7407" data-mp="l7vvwkb72f37" />
                  </button>

                  {activeMenuId === rule.id && <div className={`absolute right-0 w-56 bg-white rounded-lg shadow-lg border border-[#E2E8F0] py-1 z-20 animate-in fade-in zoom-in-95 duration-100 ${flipUp ? 'bottom-8' : 'top-8'}`} data-id="element-7408" data-mp="q5xg58kcwkg9">
                      <button onClick={e => {
                  e.stopPropagation();
                  openRule();
                }} className="w-full text-left px-4 py-2.5 text-sm text-[#0F172A] hover:bg-[#F8FAFC] flex items-center gap-2.5 transition-colors" data-id="element-7409" data-mp="158feolrcnyv0">
                        <Pencil className="w-4 h-4 text-[#64748B]" data-id="element-7410" data-mp="rcbw5ppu9eyd" />
                        {isDraft ? 'Continue editing' : 'Edit Rule'}
                      </button>
                      <button onClick={e => {
                  e.stopPropagation();
                  handleDuplicate(rule);
                }} className="w-full text-left px-4 py-2.5 text-sm text-[#0F172A] hover:bg-[#F8FAFC] flex items-center gap-2.5 transition-colors" data-id="element-7411" data-mp="1j1olqfrts8ad">
                        <Copy className="w-4 h-4 text-[#64748B]" data-id="element-7412" data-mp="26rr7ke55ci0j" />
                        Duplicate Rule
                      </button>
                      {!isDraft && <>
                          <button onClick={e => {
                    e.stopPropagation();
                    setActiveMenuId(null);
                    navigate(`/operations/activity-log?ruleId=${rule.id}`);
                  }} className="w-full text-left px-4 py-2.5 text-sm text-[#0F172A] hover:bg-[#F8FAFC] flex items-center gap-2.5 transition-colors" data-id="element-7413" data-mp="21yy3ztlezfq1">
                            <Clock className="w-4 h-4 text-[#64748B]" data-id="element-7414" data-mp="3rh4rjmjiqz7t" />
                            View History
                          </button>
                          {!isTesting && <button onClick={e => {
                    e.stopPropagation();
                    togglePause(rule);
                    setActiveMenuId(null);
                  }} className="w-full text-left px-4 py-2.5 text-sm text-[#0F172A] hover:bg-[#F8FAFC] flex items-center gap-2.5 transition-colors" data-id="element-7415" data-mp="3ihi1fww8pfu2">
                              {rule.status === 'active' ? <>
                                  <Pause className="w-4 h-4 text-[#64748B]" data-id="element-7416" data-mp="11r1hwt685zz1" />
                                  Pause Rule
                                </> : <>
                                  <Play className="w-4 h-4 text-[#64748B]" data-id="element-7417" data-mp="2raj5qbykg936" />
                                  Resume Rule
                                </>}
                            </button>}
                        </>}
                      <div className="h-px bg-[#E2E8F0] my-1" data-id="element-7418" data-mp="31144q0i5jfmm" />
                      <button onClick={e => {
                  e.stopPropagation();
                  setDeleteRuleId(rule.id);
                  setActiveMenuId(null);
                }} className="w-full text-left px-4 py-2.5 text-sm text-[#EF4444] hover:bg-[#FEF2F2] flex items-center gap-2.5 transition-colors" data-id="element-7419" data-mp="2w81hea3yx2ev">
                        <Trash2 className="w-4 h-4" data-id="element-7420" data-mp="3jy59bk7sndq1" />
                        Delete Rule
                      </button>
                    </div>}
                </div>
              </div>;
        })}
        </div>

        <div data-id="element-7421" data-mp="1pkw7melhowic">
          <div className="mb-4" data-id="element-7422" data-mp="1pmqjz4ip3gat">
            <h2 className="text-lg font-semibold text-[#0F172A]" data-id="element-7423" data-mp="1km2syiuguzgl">Templates</h2>
            <p className="text-sm text-[#64748B]" data-id="element-7424" data-mp="1m2qdp6r4crtz">
              Start with a pre-built rule and customise it to your winery. You
              choose which tag it applies.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-7425" data-mp="1w0wc3edpvfu3">
            {TEMPLATES.map(template => {
            const Icon = TEMPLATE_ICONS[template.icon] || Workflow;
            return <div key={template.key} className="bg-white border border-[#E2E8F0] rounded-lg p-5 flex flex-col hover:border-[#CBD5E1] transition-colors" data-id="element-7426" data-mp="20xmtc519chh3">
                  <div className="flex items-center gap-2 mb-3" data-id="element-7427" data-mp="18cd28uf0quzw">
                    <Icon className="w-8 h-8 text-[#0EA5E9]" data-id="element-7428" data-mp="1daoetcwp7jlt" />
                    {template.payload.ruleType === 'scored' && <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-[#EDE9FE] text-[#5B21B6] uppercase tracking-wide" data-id="element-7429" data-mp="1i1my8lczgxta">
                        Scored
                      </span>}
                  </div>
                  <h3 className="text-sm font-semibold text-[#0F172A] mb-1" data-id="element-7430" data-mp="pjj0j26eozdm">
                    {template.name}
                  </h3>
                  <p className="text-xs text-[#64748B] mb-4 flex-1" data-id="element-7431" data-mp="32szi5nxofj2w">
                    {template.description}
                  </p>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/settings/automation-rules/create', {
                state: {
                  template: template.payload
                }
              })} data-id="element-7432" data-mp="2ttw08na8x9pz">
                    Use Template
                  </Button>
                </div>;
          })}
          </div>
        </div>

        {toast && <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 max-w-lg w-[calc(100%-3rem)] animate-in fade-in slide-in-from-bottom-2 duration-200" data-id="element-7433" data-mp="2f47zpo9w2f3b">
            <div className="bg-[#0F172A] text-white rounded-lg shadow-xl px-4 py-3 flex items-start gap-3" data-id="element-7434" data-mp="265ma59601zt1">
              <CheckCircle2 className="w-5 h-5 text-[#34D399] flex-shrink-0 mt-0.5" data-id="element-7435" data-mp="214orywkzp5rj" />
              <p className="text-sm leading-relaxed flex-1" data-id="element-7436" data-mp="1w6dg6gb4wl6o">{toast}</p>
              <button onClick={() => setToast(null)} aria-label="Dismiss" className="text-[#94A3B8] hover:text-white transition-colors flex-shrink-0" data-id="element-7437" data-mp="1mj17faavjz85">
                <X className="w-4 h-4" data-id="element-7438" data-mp="1rixddf34u0qc" />
              </button>
            </div>
          </div>}

        {/* Kill switch confirmation */}
        {showKillSwitch && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-7439" data-mp="1hjoi8iwj6g2e">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200" onClick={() => setShowKillSwitch(false)} data-id="element-7440" data-mp="259ivh1kri6n6" />
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 animate-in zoom-in-95 duration-200" data-id="element-7441" data-mp="2a7amltdl1oqb">
              <div className="w-12 h-12 rounded-full bg-[#FEE2E2] flex items-center justify-center mb-4 mx-auto" data-id="element-7442" data-mp="1wgyuhugzzpz6">
                <ShieldAlert className="w-6 h-6 text-[#EF4444]" data-id="element-7443" data-mp="20gfyskkouh2r" />
              </div>
              <h3 className="text-lg font-semibold text-[#0F172A] mb-2 text-center" data-id="element-7444" data-mp="2o73i02020t4m">
                {allPaused ? 'Resume all rules?' : 'Pause all rules?'}
              </h3>
              <p className="text-sm text-[#64748B] mb-6 leading-relaxed text-center" data-id="element-7445" data-mp="hitgqtq029i9">
                {allPaused ? 'Every paused rule goes back to running on the next daily run.' : 'Every rule stops evaluating immediately. Nothing is tagged, queued or changed until you turn them back on. Anything already applied stays on members.'}
              </p>
              <div className="flex gap-3" data-id="element-7446" data-mp="3uqo3odutihhf">
                <Button variant="secondary" className="flex-1 justify-center" onClick={() => setShowKillSwitch(false)} data-id="element-7447" data-mp="3urfqp0acn6w3">
                  Cancel
                </Button>
                <Button variant={allPaused ? 'primary' : 'danger'} className="flex-1 justify-center" onClick={handleKillSwitch} data-id="element-7448" data-mp="8jzeontwgqxb">
                  {allPaused ? 'Resume all' : 'Pause all'}
                </Button>
              </div>
            </div>
          </div>}

        {deleteRuleId && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-7449" data-mp="1ezifufnkexny">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity animate-in fade-in duration-200" onClick={() => setDeleteRuleId(null)} data-id="element-7450" data-mp="1f1uv2l1xutxb" />
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 animate-in zoom-in-95 duration-200 scale-100" data-id="element-7451" data-mp="3di3ob6uq1u8p">
              <div className="w-12 h-12 rounded-full bg-[#FEE2E2] flex items-center justify-center mb-4 mx-auto" data-id="element-7452" data-mp="3ielqbyyo3ehc">
                <Trash2 className="w-6 h-6 text-[#EF4444]" data-id="element-7453" data-mp="3s6i38g5ex95m" />
              </div>
              <h3 className="text-lg font-semibold text-[#0F172A] mb-2 text-center" data-id="element-7454" data-mp="3s8kqisf6ko6n">
                Delete this rule?
              </h3>
              <p className="text-sm text-[#64748B] mb-6 leading-relaxed text-center" data-id="element-7455" data-mp="518xwqv422bi">
                Anything this rule applied stays on members but will no longer be
                automatically managed. This cannot be undone.
              </p>
              <div className="flex gap-3" data-id="element-7456" data-mp="26uzurfwgp6aw">
                <Button variant="secondary" className="flex-1 justify-center" onClick={() => setDeleteRuleId(null)} data-id="element-7457" data-mp="2brgjab7kpmoi">
                  Cancel
                </Button>
                <Button variant="danger" className="flex-1 justify-center" onClick={handleDelete} data-id="element-7458" data-mp="24aa9ntq0uvhd">
                  Delete
                </Button>
              </div>
            </div>
          </div>}
      </div>
    </>;
}